import { DirectionalIcon, EmptyState, SkeletonList, typeScale } from '@batiflow/ui';
import {
  Badge,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import {
  Add20Regular,
  ChevronRight16Regular,
  Folder20Regular,
  Folder24Regular,
  LockClosed16Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { WriteGuard, useOrganizationWritable } from '@/features/billing';
import { useFolder, useFolderChildren } from './api';
import { DocumentsList } from './documents-list';
import { FolderMenu, type FolderAction } from './folder-menu';
import { canManage, canWrite, type Folder } from './model';
import { UploadZone } from './upload-zone';
import type { Uploads } from './use-uploads';

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358","Bgto7am":"fuzgk9b"},"crumbs":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","i8kkvl":"f14mj54c","Belr9w4":"f1j0q4x9"},"crumb":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f14mj54c"},"current":{"Bhrd7zp":"fl43uef","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv","Btd35i7":"fjksvth"},"hidden":{"sj55zd":"f11d4kpn","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv"},"srOnly":{"qhf8xq":"f1euv43f","Bns2gvi":"fittdk8","st59p4":"f156r0dy","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","Bgl5zvf":"f1wch0ki","Huce71":"fz5stix"},"head":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","Brf1p80":"f1869bpl","i8kkvl":"f1xcgk68","Belr9w4":"f1wn3y0l"},"title":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","mc9l5x":"f22iagw","Bt984gj":"f122n59","Eh141a":"fni485r","i8kkvl":"fsnqrgy","Be2twd7":"f4ybsrx","Bg96gwp":"f8xlz6g","Bhrd7zp":"fl43uef","Btd35i7":"fjksvth"},"tools":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1ufnopg"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9"},"row":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Bnt446c":"f1tvncnh","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"fxugw4r","Jwef8y":"f1knas48"},"open":{"Bh6795r":"fqerorx","Brf1p80":"fbhxue7","fsow6f":"fpgzoln","Bgto7am":"fuzgk9b"},"name":{"Btd35i7":"fjksvth"}});

export interface FolderContentProps {
  /** Null in another organisation's space (no organisation-level reads). */
  orgId: string | null;
  spaceId: string;
  /** The project's code (the `{PROJECT}` token of the naming pattern). */
  projectCode: string | null;
  /** The open folder; null is the root of the project. */
  folderId: string | null;
  /** The caller manages the project: a folder may be created at the root. */
  canManageRoot: boolean;
  /** The uploads of the tab: the files being added, here and in other folders. */
  uploads: Uploads;
  /** The "Permissions" action needs the organisation's members: not offered without them. */
  canSetPermissions: boolean;
  /** The caller manages the space: transmittals (GEDX-FE-02) and share links (GEDX-FE-03) are offered. */
  canTransmit?: boolean;
  /** Opens a folder (null: the root); `ancestors` are the ids to unfold in the tree. */
  onOpen: (id: string | null, ancestors: readonly string[]) => void;
  /** `parent` is the folder a new folder goes in (null: the root). */
  onCreate: (parent: Folder | null) => void;
  onAction: (action: FolderAction, folder: Folder) => void;
  /** A document of the list was activated: the right panel opens on it. */
  onOpenDocument: (documentId: string) => void;
  /** Documents left the open folder (moved or deleted). */
  onDocumentsGone: (documentIds: readonly string[]) => void;
}

/**
 * The open folder: breadcrumbs (an ancestor the caller cannot read shows as "…", without a
 * link), its title with the actions its permissions allow, its subfolders, the upload zone and
 * the files list (GED-FE-03).
 */
export function FolderContent({
  orgId,
  spaceId,
  projectCode,
  folderId,
  canManageRoot,
  uploads,
  canSetPermissions,
  canTransmit = false,
  onOpen,
  onCreate,
  onAction,
  onOpenDocument,
  onDocumentsGone,
}: FolderContentProps) {
  const { t } = useTranslation('documents');
  const { writable } = useOrganizationWritable();
  const styles = useStyles();
  const detail = useFolder(spaceId, folderId);
  const children = useFolderChildren(spaceId, folderId);

  if (folderId !== null && detail.isError && !detail.data) {
    return (
      <MessageBar intent="error" layout="multiline">
        <MessageBarBody>{t('content.loadFailed')}</MessageBarBody>
        <MessageBarActions>
          <Button
            onClick={() => {
              void detail.refetch();
            }}
          >
            {t('common:actions.retry')}
          </Button>
          <Button
            onClick={() => {
              onOpen(null, []);
            }}
          >
            {t('breadcrumb.root')}
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  }
  if (folderId !== null && !detail.data) return <SkeletonList count={4} />;

  const folder = folderId !== null ? (detail.data ?? null) : null;
  const ancestors = folder?.breadcrumbs ?? [];
  const mayCreate = folder ? canWrite(folder) : canManageRoot;
  const subfolders = children.data?.items ?? [];

  return (
    <div className={styles.root}>
      <nav aria-label={t('breadcrumb.label')}>
        <ol className={styles.crumbs}>
          <li className={styles.crumb}>
            {folder ? (
              <Button
                appearance="transparent"
                size="small"
                onClick={() => {
                  onOpen(null, []);
                }}
              >
                {t('breadcrumb.root')}
              </Button>
            ) : (
              <span className={styles.current} aria-current="page">
                {t('breadcrumb.root')}
              </span>
            )}
          </li>
          {ancestors.map((ancestor, index) => (
            <li key={ancestor.id} className={styles.crumb}>
              <DirectionalIcon icon={ChevronRight16Regular} aria-hidden="true" />
              {ancestor.name === null ? (
                <span className={styles.hidden}>
                  <span aria-hidden="true">…</span>
                  <span className={styles.srOnly}>{t('breadcrumb.hidden')}</span>
                </span>
              ) : (
                <Button
                  appearance="transparent"
                  size="small"
                  onClick={() => {
                    onOpen(
                      ancestor.id,
                      ancestors.slice(0, index).map((other) => other.id),
                    );
                  }}
                >
                  <bdi>{ancestor.name}</bdi>
                </Button>
              )}
            </li>
          ))}
          {folder ? (
            <li className={styles.crumb}>
              <DirectionalIcon icon={ChevronRight16Regular} aria-hidden="true" />
              <span className={styles.current} aria-current="page">
                <bdi>{folder.name}</bdi>
              </span>
            </li>
          ) : null}
        </ol>
      </nav>

      <div className={styles.head}>
        <h2 className={styles.title}>
          {folder ? <bdi>{folder.name}</bdi> : t('breadcrumb.root')}
          {folder?.aclMode === 'custom' ? (
            <Badge appearance="outline" color="informative" icon={<LockClosed16Regular />}>
              {t('content.customRights')}
            </Badge>
          ) : null}
        </h2>
        <div className={styles.tools}>
          {mayCreate ? (
            <WriteGuard>
              <Button
                appearance="primary"
                icon={<Add20Regular />}
                disabledFocusable={!writable}
                onClick={() => {
                  onCreate(folder);
                }}
              >
                {t('actions.newFolder')}
              </Button>
            </WriteGuard>
          ) : null}
          {folder && canManage(folder) ? (
            <FolderMenu
              withPermissions={canSetPermissions}
              withShare={canTransmit}
              onAction={(action) => {
                onAction(action, folder);
              }}
            />
          ) : null}
        </div>
      </div>

      {folder && canWrite(folder) ? (
        <UploadZone
          uploads={uploads}
          target={{ spaceId, folderId: folder.id }}
          naming={{
            orgId,
            projectCode,
            // The open folder first, then its ancestors (a hidden one has no name).
            folderNames: [
              folder.name,
              ...[...ancestors]
                .reverse()
                .flatMap((ancestor) => (ancestor.name ? [ancestor.name] : [])),
            ],
          }}
        />
      ) : null}

      {children.isError ? (
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>{t('tree.loadFailed')}</MessageBarBody>
          <MessageBarActions>
            <Button
              onClick={() => {
                void children.refetch();
              }}
            >
              {t('common:actions.retry')}
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : !children.data ? (
        <SkeletonList count={4} />
      ) : subfolders.length > 0 ? (
        <ul className={styles.list} aria-label={t('content.foldersLabel')}>
          {subfolders.map((child) => (
            <li key={child.id} className={styles.row}>
              <Button
                appearance="transparent"
                className={styles.open}
                icon={<Folder20Regular />}
                onClick={() => {
                  onOpen(child.id, [
                    ...ancestors.map((other) => other.id),
                    ...(folder ? [folder.id] : []),
                  ]);
                }}
              >
                <bdi className={styles.name}>{child.name}</bdi>
              </Button>
              {child.aclMode === 'custom' ? (
                <Badge appearance="outline" color="informative" icon={<LockClosed16Regular />}>
                  {t('content.customRights')}
                </Badge>
              ) : null}
              {canManage(child) ? (
                <FolderMenu
                  folderName={child.name}
                  withPermissions={canSetPermissions}
                  withShare={canTransmit}
                  onAction={(action) => {
                    onAction(action, child);
                  }}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : folder === null ? (
        <EmptyState
          headingLevel={3}
          illustration={<Folder24Regular />}
          title={t('content.rootEmpty.title')}
          description={
            canManageRoot
              ? t('content.rootEmpty.descriptionManage')
              : t('content.rootEmpty.descriptionRead')
          }
          {...(canManageRoot
            ? {
                primaryAction: {
                  label: t('actions.newFolder'),
                  onClick: () => {
                    onCreate(null);
                  },
                },
              }
            : {})}
        />
      ) : null}

      {folder ? (
        <>
          {children.isSuccess && subfolders.length === 0 ? (
            <p>{t('content.noFolders.description')}</p>
          ) : null}
          <DocumentsList
            key={folder.id}
            spaceId={spaceId}
            folderId={folder.id}
            canAdd={canWrite(folder)}
            canTransmit={canTransmit}
            onOpenDocument={onOpenDocument}
            onGone={onDocumentsGone}
          />
        </>
      ) : null}
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/folder-content.griffel.css";